import React, { useEffect, useMemo, useState } from 'react';
import { FaCheck, FaDownload, FaTimes } from 'react-icons/fa';
import Modal from '../../../components/common/Modal';
import { API_BASE_URL } from '../../../config';

const ID_TYPE_MAP = {
  1: 'South African Smart ID Card',
  2: 'South African ID Document',
  3: 'Business Registration Document',
  4: 'Passport'
};

const buildDocumentUrl = (filePath) => {
  if (!filePath) {
    return null;
  }

  if (filePath.startsWith('http')) {
    return `${filePath}?v=${Date.now()}`;
  }

  const normalized = filePath.replace(/^\/+/, '');

  if (normalized.startsWith('uploads/') || normalized.startsWith('attachments/')) {
    return `${API_BASE_URL}/${normalized}?v=${Date.now()}`;
  }

  return `${API_BASE_URL}/design/assets/${normalized}?v=${Date.now()}`;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return '—';
  }

  const value = Number(timestamp);
  if (Number.isNaN(value)) {
    return timestamp;
  }

  return new Date(value * 1000).toLocaleString();
};

function CustomerReviewModal({
  isOpen,
  customer,
  onClose,
  onSave,
  onConfirm,
  onReject,
  saving = false,
  processing = false,
  rejecting = false,
  canDecide = false
}) {
  const [formState, setFormState] = useState({ new_name: '', new_id: '' });

  useEffect(() => {
    if (customer) {
      setFormState({
        new_name: customer.new_name || '',
        new_id: customer.new_id || ''
      });
    }
  }, [customer]);

  const isDirty = useMemo(() => {
    if (!customer) {
      return false;
    }
    return (
      (customer.new_name || '') !== formState.new_name ||
      (customer.new_id || '') !== formState.new_id
    );
  }, [customer, formState]);

  const attachments = useMemo(() => {
    if (!customer) {
      return [];
    }

    const files = [];
    if (customer.id_front) {
      files.push({
        key: 'front',
        label: 'Identification Front',
        path: customer.id_front
      });
    }
    if (customer.id_back) {
      files.push({
        key: 'back',
        label: 'Identification Back',
        path: customer.id_back
      });
    }

    return files.map((file) => ({
      ...file,
      url: buildDocumentUrl(file.path)
    }));
  }, [customer]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={customer ? `Account ${customer.account}` : 'Customer Review'}
      size="xl"
    >
      {customer ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div>
              <p className="text-sm text-gray-600">Account Name</p>
              <p className="text-base font-medium text-gray-900">
                {customer.account_name || '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Submission Timestamp</p>
              <p className="text-base font-medium text-gray-900">
                {formatTimestamp(customer.submitted)}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm">
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Customer Name</td>
                  <td className="px-4 py-3 text-gray-700">{customer.account_name || '—'}</td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      name="new_name"
                      value={formState.new_name}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">ID Number</td>
                  <td className="px-4 py-3 text-gray-700">{customer.account_id || '—'}</td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      name="new_id"
                      value={formState.new_id}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">ID Type</td>
                  <td className="px-4 py-3 text-gray-700">
                    {ID_TYPE_MAP[customer.id] || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {attachments.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {attachments.map((file) => (
                          file.url ? (
                            <a
                              key={file.key}
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition"
                            >
                              <FaDownload className="mr-2" />
                              {file.label}
                            </a>
                          ) : (
                            <span
                              key={file.key}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded"
                            >
                              {file.label}
                            </span>
                          )
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500">No documents supplied</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Mobile Number</td>
                  <td className="px-4 py-3 text-gray-700">{customer.account_mobile || '—'}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {customer.new_mobile || <span className="text-gray-500">—</span>}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Email Address</td>
                  <td className="px-4 py-3 text-gray-700 whitespace-pre-wrap break-words max-w-[260px]">
                    {customer.account_email || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-900 whitespace-pre-wrap break-words max-w-[260px]">
                    {customer.new_email || <span className="text-gray-500">—</span>}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onSave(formState)}
                disabled={!isDirty || saving}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border transition ${
                  !isDirty || saving
                    ? 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'border-blue-500 text-blue-700 bg-blue-50 hover:bg-blue-100'
                }`}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              {!canDecide && (
                <span className="text-xs text-gray-500 self-center">
                  Decisions are only available for received submissions.
                </span>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-2 md:ml-auto">
              <button
                type="button"
                onClick={onReject}
                disabled={rejecting || !canDecide}
                className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border transition ${
                  rejecting || !canDecide
                    ? 'border-red-200 text-red-300 bg-red-50 cursor-not-allowed'
                    : 'border-red-500 text-red-700 bg-red-50 hover:bg-red-100'
                }`}
              >
                {rejecting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-red-300 border-t-transparent rounded-full" />
                    Rejecting…
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <FaTimes />
                    Reject Submission
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={onConfirm}
                disabled={processing || !canDecide}
                className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border transition ${
                  processing || !canDecide
                    ? 'border-green-200 text-green-300 bg-green-50 cursor-not-allowed'
                    : 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100'
                }`}
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-green-300 border-t-transparent rounded-full" />
                    Confirming…
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <FaCheck />
                    Confirm Submission
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-600">No customer selected.</p>
      )}
    </Modal>
  );
}

export default CustomerReviewModal;
